import { Box, Stack, Typography } from "@mui/material";
import ScoutIcon from "components/ScoutIcon";

import type { ScoutAttachment } from "../scoutSubmitAttachments";

import { ScoutChatDropzone } from "./ScoutChatDropzone";
import { ScoutInput, type ScoutSubmitHandler } from "./ScoutInput";
import { ScoutProFooterUpsell } from "./ScoutProFooterUpsell";
import { SuggestionMenu } from "./SuggestionMenu";

export function NewChat({
  tenantId,
  onSubmit,
  onAbort,
  isSending,
  isAborting,
  onAttachmentsUploaded,
  onAttachmentRemoved,
  allowMutations = false,
  onAllowMutationsChange,
  hasScoutProAccess = true,
}: {
  tenantId?: string;
  onSubmit: ScoutSubmitHandler;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
  onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
  onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
  allowMutations?: boolean;
  onAllowMutationsChange?: (next: boolean) => void;
  hasScoutProAccess?: boolean;
}) {
  return (
    <ScoutChatDropzone
      sx={{
        flex: 1,
        alignSelf: "stretch",
        width: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Scroll container: centers the hero via `margin: auto` when there's
          room, but lets everything scroll into view (nothing clipped) once the
          viewport is too short — e.g. at high browser zoom. */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          sx={{
            width: "100%",
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 2,
            // Clear the floating header overlay so the logo is never tucked
            // behind it, and bias the block slightly upward for optical balance.
            paddingTop: { xs: 8, sm: 9 },
            paddingBottom: { xs: 12, sm: 16 },
            gap: 5,
          }}
        >
          <ScoutIcon sx={{ width: 80, height: 80 }} />
          <Typography variant="h6" textAlign="center">
            Hey there! I'm Scout, your Encamp guide. How can I help you today?
          </Typography>

          <ScoutInput
            tenantId={tenantId}
            conversationId={undefined}
            onSubmit={onSubmit}
            onAbort={onAbort}
            isSending={isSending}
            isAborting={isAborting}
            onAttachmentsUploaded={onAttachmentsUploaded}
            onAttachmentRemoved={onAttachmentRemoved}
            placeholder="Check regulations, help me understand compliance requirements..."
            compactPlaceholder="Ask Scout a question…"
            allowMutations={allowMutations}
            onAllowMutationsChange={onAllowMutationsChange}
            hasScoutProAccess={hasScoutProAccess}
          />

          <SuggestionMenu onPick={(prompt) => onSubmit(prompt)} disabled={isSending} />
        </Stack>
      </Box>
      {!hasScoutProAccess ? <ScoutProFooterUpsell /> : null}
    </ScoutChatDropzone>
  );
}
